import { Box, Flex, Skeleton } from '@radix-ui/themes';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CategoryDistribution } from './CategoryDistribution';
import { CommercialOfferDistribution } from './CommercialOfferDistribution';
import { DataCard } from './DataCard';
import {
    applyChartFormatting,
    formatLabel,
    groupData,
    mergeCategoryMetrics,
    RawBarData,
} from './metricsHelpers';
import { useMetricsCollection } from '@features/Collection/hooks/useMetricsCollection';
import { useCollections } from '@features/Collection';
import { GetClipsArgs, useClips } from '@features/Clip';
import { FixedFilterGenerator } from '@common/types/queryState';

const lastMonthFilter: FixedFilterGenerator<GetClipsArgs> = () => [
    'mediaObjectCreated:[NOW-1MONTH TO NOW]',
];

export const MetricsSection = () => {
    const { t } = useTranslation('dashboard');
    const { metrics, isLoading } = useMetricsCollection();

    const { itemCount } = useCollections({ limit: 0 });
    const { itemCount: ingested } = useClips(
        { limit: 0 },
        { fixedFilterGenerators: [lastMonthFilter] },
    );

    const rawCategoryData = React.useMemo(
        () =>
            mergeCategoryMetrics(metrics.categoryVolume, metrics.categoryCount),
        [metrics.categoryVolume, metrics.categoryCount],
    );

    const commercialData = React.useMemo(() => {
        const otherLabel = t('others');

        const rawOffers: RawBarData[] = metrics.offerVolume.map(
            ({ name, value }) => ({ name: formatLabel(name), value }),
        );

        const grouped = groupData(
            rawOffers,
            5,
            (a, b) => b.value - a.value,
            (tail, label) => ({
                name: label,
                value: tail.reduce((sum, item) => sum + item.value, 0),
            }),
            otherLabel,
        );

        return applyChartFormatting(grouped, otherLabel);
    }, [metrics.offerVolume, t]);

    if (isLoading) {
        return (
            <Flex gap={'3'} width={'100%'} px={'1'}>
                <Skeleton width='300px' height='300px' />
                <Skeleton width='100%' height='300px' />
                <Skeleton width='300px' height='300px' />
                <Skeleton width='100%' height='300px' />
            </Flex>
        );
    }

    return (
        <Flex gap={'3'} width={'100%'} px={'1'}>
            <Flex direction={'column'} gap={'3'}>
                <DataCard
                    title={t('totalPrograms.title')}
                    content={itemCount.toLocaleString()}
                />
                <DataCard
                    title={t('totalIngests.title')}
                    content={ingested}
                    subTitle={t('totalIngests.subTitle')}
                />
            </Flex>

            <Box flexGrow={'1'}>
                <CommercialOfferDistribution data={commercialData} />
            </Box>

            <Box flexGrow={'1'}>
                <CategoryDistribution
                    rawData={rawCategoryData}
                    totalContent={metrics.assetCount ?? 0}
                    totalSize={metrics.volume ?? 0}
                    lastUpdated={
                        metrics.lastAnalysis
                            ? new Date(metrics.lastAnalysis)
                            : new Date()
                    }
                />
            </Box>
        </Flex>
    );
};
